import {
	Component,
	OnDestroy,
	OnInit,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	inject,
} from '@angular/core';
import { AsyncPipe } from '@angular/common';
import { MatSnackBar, MatSnackBarRef } from '@angular/material/snack-bar';
import { NavigationEnd, Router } from '@angular/router';
import {
	SwUpdate,
	VersionInstallationFailedEvent,
} from '@angular/service-worker';

import { JepSnackbarComponent } from '@jepto/shared/components/jep-snackbar/jep-snackbar.component';
import { CoreModule } from '@jepto/core/core.module';

import { JepCookiesService } from '@jepto/shared/services/jep-cookies/jep-cookies.service';
import { JepDecodedJWTService } from '@jepto/shared/services/jep-decoded-jwt/jep-decode-jwt.service';
import { JepGlobalDataService } from '@jepto/shared/services/jep-global-data/jep-global-data.service';

import * as fromSignInRootStore from '@jepto/features/signin/store/index';
import * as fromSharedStore from '@jepto/shared/store/index';
import * as fromSharedUsersStore from '@jepto/shared/store/users/index';
import { Store } from '@ngrx/store';

import { environment } from '@jepto/environments/environment';
import { sortKeySplitter } from '@jepto/shared/utils/collection';
import { safeFetchAuthSession } from '@jepto/shared/utils/fetch-auth-session.util';
import { log, warn } from '@jepto/shared/utils/logger';
import { reportErrorToSentry } from '@jepto/shared/utils/report-error-to-sentry.util';
import {
	applyOrttoVerifiedTalkSession,
	applyOrttoVerifiedTalkSessionWhenReady,
	canRetryOrttoTalkToken,
	isOrttoClientReady,
	matchOrttoUserByEmail,
	resolveOrttoTrackingSkip,
	shouldSkipOrttoTrackForSortKey,
	type OrttoTrackingClient,
	type OrttoTrackingUserDetails,
} from '@jepto/shared/utils/ortto-tracking.util';
import { CookieStorage } from 'amazon-cognito-identity-js';
import {
	BehaviorSubject,
	Observable,
	Subject,
	combineLatest,
	from,
	timer,
} from 'rxjs';
import {
	debounceTime,
	distinctUntilChanged,
	filter,
	map,
	switchMap,
	take,
	takeUntil,
} from 'rxjs/operators';

import { markForCheckOnEmit } from '@jepto/shared/utils/change-detection';
declare var $: any;

@Component({
	selector: 'jep-root',
	templateUrl: './app.component.html',
	styleUrls: ['./app.component.scss'],
	changeDetection: ChangeDetectionStrategy.Eager,
	// CoreModule for <jep-container-layout>. Note: importing it into a
	// standalone root gives this component tree its own instances of
	// CoreModule/SharedModule-provided services (see app.config.ts).
	imports: [CoreModule, AsyncPipe],
})
export class AppComponent implements OnInit, OnDestroy {
	private readonly cdr = inject(ChangeDetectorRef);
	authRoutes: string[] = [
		'sign-in',
		'sign-up',
		'forgot-password',
		'change-password',
	];
	noLayoutRoutes: string[] = [
		'create-password',
		'reset-password',
		'plans',
		'subscribe',
		'resubscribe',
	];
	noLayout: boolean;
	showAuthLayout: boolean;
	layout$ = new BehaviorSubject<any>(null);
	showAuthLayout$ = new BehaviorSubject<any>(null);
	unsubscribe$ = new Subject<void>();
	jwt: any;
	accountDetails: any;
	userDetails: OrttoTrackingUserDetails | null;
	accountDetails$: Observable<any>;
	userDetails$: Observable<any>;
	isUserLoggedIn$: Observable<boolean>;
	getUsers$: Observable<any>;
	subscriptionIndicatorMessage: string =
		'Your trial of the Ageny Starter Plan ends in 3days';
	onGetURL = new Subject();
	publicIPaddress: string;
	/** Set only after Ortto track is started for a user — not on missing-sdk skips. */
	#orttoTrackedSortKey: string | null = null;
	#orttoSdkWaitInFlight = false;
	/** Failed Talk token requests for the current user; caps the retry loop. */
	#orttoTalkTokenFailures = { sortKey: '', count: 0 };
	/** A new SW version is waiting; keeps the prompt re-showable until the user reloads. */
	#updateReady = false;
	/** The open update prompt, so navigation does not stack duplicates. */
	#updatePromptRef: MatSnackBarRef<JepSnackbarComponent> | null = null;

	constructor(
		private router: Router,
		private userStore: Store<fromSharedUsersStore.UsersModuleReducer>,
		private sharedStore: Store<fromSharedStore.SharedModuleReducers>,
		private jepDecodedJwtService: JepDecodedJWTService,
		private JepGlobalDataService: JepGlobalDataService,
		private store: Store<fromSignInRootStore.SignInModuleReducer>,
		private swUpdate: SwUpdate,
		private matSnackBar: MatSnackBar,
		private jepCookieService: JepCookiesService
	) {
		this.listenForRouterEvents();
	}

	ngOnInit() {
		localStorage.removeItem('invoke_insight');
		localStorage.removeItem('client_budget_planner');
		this.selectors();
		this.initialiseJeptoCoreLayouts();
		this.setupServiceWorkerUpdates();
	}

	ngOnDestroy() {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
	}

	/**
	 * Triggers the service worker to check for updates (on navigation).
	 * Does not activate or reload; that happens only when the user confirms
	 * via the snackbar after a VERSION_READY event.
	 *
	 * Also re-shows the prompt if it was displaced: `versionUpdates` fires VERSION_READY
	 * once per version, and MatSnackBar only holds one snackbar at a time, so any other
	 * message would otherwise bury the update prompt for good.
	 */
	runNewAppVersionNotifier() {
		if (!this.swUpdate.isEnabled) {
			return;
		}
		if (this.#updateReady) {
			this.promptUserToReload();
			return;
		}
		// Rejects on transient network failures; the next navigation retries.
		this.swUpdate.checkForUpdate().catch(() => undefined);
	}

	/**
	 * Subscribes to service worker version and unrecoverable events.
	 * - VERSION_READY: show snackbar; activate and reload only when the user clicks.
	 * - VERSION_INSTALLATION_FAILED: report to Sentry — the user gets no prompt and
	 *   the app silently stops updating, so this must never fail quietly (JEP-10472).
	 * - Unrecoverable: force reload to recover from SW failure (avoids blank screen).
	 */
	private setupServiceWorkerUpdates() {
		if (!this.swUpdate.isEnabled) {
			return;
		}
		this.swUpdate.versionUpdates
			.pipe(markForCheckOnEmit(this.cdr), takeUntil(this.unsubscribe$))
			.subscribe(evt => {
				if (evt.type === 'VERSION_READY') {
					this.#updateReady = true;
					this.promptUserToReload();
					return;
				}
				if (evt.type === 'VERSION_INSTALLATION_FAILED') {
					this.reportVersionInstallationFailure(evt);
				}
			});
		this.swUpdate.unrecoverable
			.pipe(markForCheckOnEmit(this.cdr), takeUntil(this.unsubscribe$))
			.subscribe(() => window.location.reload());
	}

	/**
	 * A failed install means the new version can never activate, so the update prompt
	 * never shows. The usual cause is a hash mismatch between ngsw.json and the
	 * deployed assets (build output modified after ngsw.json was generated).
	 */
	private reportVersionInstallationFailure(
		evt: VersionInstallationFailedEvent
	) {
		reportErrorToSentry(
			'AppComponent',
			'Service worker version installation failed — users will not be prompted to update',
			new Error(evt.error || 'VERSION_INSTALLATION_FAILED'),
			{ feature: 'service-worker' }
		);
	}

	/**
	 * Stays open until the user acts (`duration: 0`). It previously auto-dismissed after
	 * 5s and reloaded from `afterDismissed()`, which reloaded the page out from under
	 * anyone who ignored it — losing unsaved report edits (JEP-10472).
	 */
	private promptUserToReload() {
		if (this.#updatePromptRef) {
			return;
		}
		const ref = this.matSnackBar.openFromComponent(JepSnackbarComponent, {
			duration: 0,
			data: {
				text: 'New version available. Please reload the page.',
				withButton: true,
				customTextButton: 'RELOAD',
				status: 'info',
			},
		});
		this.#updatePromptRef = ref;
		ref.afterDismissed()
			.pipe(markForCheckOnEmit(this.cdr), takeUntil(this.unsubscribe$))
			.subscribe(() => (this.#updatePromptRef = null));
		ref.instance.onClickSnackbar
			.pipe(
				take(1),
				markForCheckOnEmit(this.cdr),
				takeUntil(this.unsubscribe$)
			)
			.subscribe(() => {
				this.swUpdate
					.activateUpdate()
					.then(() => {
						if (this.router.url === '/sign-in') {
							const hostname = window.location.hostname;
							const c = new CookieStorage({
								domain:
									hostname === 'localhost'
										? 'localhost'
										: `${environment.key === 'prd' ? 'app' : environment.key}.jepto.com`,
								secure: hostname === 'localhost' ? false : true,
								path: '/',
							});
							c.clear();
						}
						window.location.reload();
					})
					.catch(err => {
						// The user asked to reload and nothing happened — reload anyway so
						// they are not stuck on a stale build, but record why it failed.
						reportErrorToSentry(
							'AppComponent',
							'activateUpdate() failed after the user accepted the update prompt',
							err,
							{ feature: 'service-worker' }
						);
						window.location.reload();
					});
			});
	}

	initialiseJeptoCoreLayouts() {
		// Avoid AppSync GraphQL while on sign-in/sign-up: stale idToken cookies can still
		// yield an accountId while Amplify has no usable JWT (see Sentry JEPTO-130).
		if (!this.includesURL(this.router.url, this.authRoutes)) {
			this.JepGlobalDataService.loadCurrentAccount('app component');
			this.JepGlobalDataService.loadClients('app component');
		}
		if (Boolean(localStorage.getItem('businessClientId'))) {
			localStorage.removeItem('businessClientId');
		}
		this.runShowLayout();
		this.readURLValues(this.router.url);
		this.listentUserAndAccountDetails();
		if (this.jwt) {
			this.listentUserDetails();
		}
	}

	selectors() {
		this.accountDetails$ = this.sharedStore.select(
			fromSharedStore.getAccountData
		);
		this.userDetails$ = this.userStore.select(
			fromSharedUsersStore.getUsers
		);
		this.isUserLoggedIn$ = this.sharedStore.select(
			fromSharedStore.isUserLoggedIn
		);
	}

	listentUserAndAccountDetails() {
		combineLatest([this.accountDetails$, this.userDetails$])
			.pipe(
				filter(
					([accountDetails, users]) =>
						accountDetails != null &&
						Array.isArray(users) &&
						users.length > 0
				),
				switchMap(([accountDetails, users]) =>
					from(safeFetchAuthSession()).pipe(
						map(session => {
							const email = session?.tokens?.idToken?.payload
								?.email as string | undefined;
							return {
								accountDetails,
								matched: matchOrttoUserByEmail(users, email),
							};
						})
					)
				),
				filter(
					(
						result
					): result is {
						accountDetails: unknown;
						matched: OrttoTrackingUserDetails;
					} => result.matched != null
				),
				debounceTime(500),
				markForCheckOnEmit(this.cdr),
				takeUntil(this.unsubscribe$)
			)
			.subscribe(result => {
				this.accountDetails = result.accountDetails;
				this.userDetails = result.matched;
				this.trackOrtto(this.userDetails);
			});
	}

	listentUserDetails() {
		this.userDetails$
			.pipe(
				filter(
					(users): users is OrttoTrackingUserDetails[] =>
						Array.isArray(users) && users.length > 0
				),
				switchMap(users =>
					from(safeFetchAuthSession()).pipe(
						map(session => {
							const email = session?.tokens?.idToken?.payload
								?.email as string | undefined;
							return matchOrttoUserByEmail(users, email);
						})
					)
				),
				markForCheckOnEmit(this.cdr),
				takeUntil(this.unsubscribe$)
			)
			.subscribe(matched => {
				this.userDetails = matched;
				// log() does not redact payloads (see shared/utils/logger); do not pass user/PII.
				log('userDetails', {
					matchedCurrentUser: Boolean(this.userDetails),
				});
			});
	}

	trackOrtto(userDetails: OrttoTrackingUserDetails | null): void {
		if (
			userDetails &&
			shouldSkipOrttoTrackForSortKey(
				this.#orttoTrackedSortKey,
				userDetails.sortKey
			)
		) {
			return;
		}

		const ortto = (window as any).ap3c as OrttoTrackingClient | undefined;
		const skipReason = resolveOrttoTrackingSkip(ortto, userDetails);

		if (skipReason === 'missing-user') {
			// Expected while session/users settle — do not warn (Sentry JEP-10310).
			return;
		}

		if (skipReason === 'missing-sdk') {
			if (!this.#orttoSdkWaitInFlight) {
				warn('Ortto tracking skipped due to missing prerequisites.', {
					hasOrttoClient: !!ortto,
					hasOrttoInitFunc:
						!!ortto && typeof ortto.init === 'function',
					hasOrttoTrackFunc:
						!!ortto && typeof ortto.track === 'function',
					hasUserDetails: !!userDetails,
				});
				this.#waitForOrttoSdkThenTrack(userDetails);
			}
			return;
		}

		if (!isOrttoClientReady(ortto) || !userDetails) {
			return;
		}

		this.#orttoTrackedSortKey = userDetails.sortKey;

		const userKey: string = sortKeySplitter(userDetails.sortKey, '#', 1);
		const fullName: string = `${userDetails.firstName} ${userDetails.lastName}`;
		this.JepGlobalDataService.generteOrttoTalkToken(
			userKey,
			userDetails.email,
			fullName
		)
			.pipe(markForCheckOnEmit(this.cdr), takeUntil(this.unsubscribe$))
			.subscribe(
				(result: { state: boolean; data: any; message: string }) => {
					if (result.state) {
						// The boot snippet in index.html already ran ap3c.init, and
						// Ortto ignores a second init, so switch the widget to the
						// verified Talk session instead.
						this.#applyOrttoTalkToken(
							userDetails.sortKey,
							ortto,
							result.data?.token
						);
						ortto.track({
							v: 0,
							ei: sortKeySplitter(userDetails.sortKey, '#', 1),
							email: userDetails.email,
							first: userDetails.firstName,
							last: userDetails.lastName,
						});
					} else {
						this.#retryOrttoTalkTokenLater(userDetails.sortKey);
					}
				},
				error => {
					this.#retryOrttoTalkTokenLater(userDetails.sortKey);
					warn('Error fetching Ortto tracking settings:', error);
				}
			);
	}

	/**
	 * Apply the Talk token now, or once Talk has loaded (it loads separately from
	 * init/track). A token that never gets applied counts as a failure so a later
	 * emission can retry.
	 */
	#applyOrttoTalkToken(
		sortKey: string,
		ortto: OrttoTrackingClient,
		token: string | null | undefined
	): void {
		const onApplied = (): void => {
			this.#orttoTalkTokenFailures = { sortKey: '', count: 0 };
		};
		if (applyOrttoVerifiedTalkSession(ortto, token)) {
			onApplied();
			return;
		}
		if (!token) {
			warn('Ortto Talk token missing from the session response.');
			this.#retryOrttoTalkTokenLater(sortKey);
			return;
		}
		applyOrttoVerifiedTalkSessionWhenReady(
			() => (window as any).ap3c as OrttoTrackingClient | undefined,
			token
		)
			.pipe(takeUntil(this.unsubscribe$))
			.subscribe(applied => {
				if (applied) {
					onApplied();
					return;
				}
				warn('Ortto Talk did not load; verified session not applied.');
				this.#retryOrttoTalkTokenLater(sortKey);
			});
	}

	/**
	 * Allow a later attempt for the same user after a Talk token failure, up to
	 * ORTTO_TALK_TOKEN_MAX_ATTEMPTS, so a broken token call does not re-fire
	 * on every store emission.
	 */
	#retryOrttoTalkTokenLater(sortKey: string): void {
		const failures = this.#orttoTalkTokenFailures;
		const count = failures.sortKey === sortKey ? failures.count + 1 : 1;
		this.#orttoTalkTokenFailures = { sortKey, count };
		if (
			canRetryOrttoTalkToken(count) &&
			this.#orttoTrackedSortKey === sortKey
		) {
			this.#orttoTrackedSortKey = null;
		}
	}

	/**
	 * Poll for the Ortto SDK after a missing-sdk skip so identify/track still
	 * runs when ap3c becomes ready (distinctUntilChanged on sortKey alone would
	 * permanently drop same-user retries).
	 */
	#waitForOrttoSdkThenTrack(
		userDetails: OrttoTrackingUserDetails | null
	): void {
		if (!userDetails || this.#orttoSdkWaitInFlight) {
			return;
		}

		this.#orttoSdkWaitInFlight = true;
		const pollMs = 250;
		const maxAttempts = 40; // ~10s

		timer(0, pollMs)
			.pipe(
				take(maxAttempts),
				map(
					() =>
						(window as any).ap3c as OrttoTrackingClient | undefined
				),
				filter(isOrttoClientReady),
				take(1),
				markForCheckOnEmit(this.cdr),
				takeUntil(this.unsubscribe$)
			)
			.subscribe({
				next: () => {
					this.#orttoSdkWaitInFlight = false;
					this.trackOrtto(userDetails);
				},
				complete: () => {
					this.#orttoSdkWaitInFlight = false;
				},
			});
	}

	runShowLayout() {
		this.showAuthLayout$
			.pipe(
				distinctUntilChanged(),
				markForCheckOnEmit(this.cdr),
				takeUntil(this.unsubscribe$)
			)
			.subscribe((URL: string) => {
				if (Boolean(URL) === true) this.readURLValues(URL);
			});
	}

	readURLValues(url: string) {
		this.showAuthLayout = this.includesURL(url, this.authRoutes);
		this.noLayout = this.includesURL(url, this.noLayoutRoutes);
		let layout: string;
		if (this.showAuthLayout && !this.noLayout) {
			layout = 'auth';
		} else if (!this.showAuthLayout && this.noLayout) {
			layout = 'noLayout';
		} else if (!this.showAuthLayout && !this.noLayout) {
			layout = 'dashboard';
		}
		this.layout$.next(layout);
	}

	listenForRouterEvents() {
		localStorage.removeItem('redirect');
		this.router.events
			.pipe(
				filter(event => event instanceof NavigationEnd),
				markForCheckOnEmit(this.cdr),
				takeUntil(this.unsubscribe$)
			)
			.subscribe((event: NavigationEnd) => {
				this.onGetURL.next(event.url);
				this.showAuthLayout$.next(event.urlAfterRedirects);
				this.runNewAppVersionNotifier();
			});
	}

	loadJWTDetails() {
		this.jwt = this.jepDecodedJwtService.getDecodedJWT();
	}

	includesURL(activeURL: any, urlsToInclude: string[]) {
		return urlsToInclude.some(url => activeURL.includes(url));
	}

	getAccountId(): string {
		const jwtDetails = this.jepDecodedJwtService.getDecodedJWT();
		return jwtDetails['custom:accountId'];
	}

	revertDefaultStateSignIn() {
		this.store.dispatch(
			new fromSignInRootStore.RevertSignInState({
				transacting: false,
				success: false,
				error: false,
			})
		);
		return;
	}

	dispatchSharedRouterAndAuthStore() {
		this.sharedStore.dispatch(
			new fromSharedStore.LogoutUser({
				isLoggedIn: false,
				isLoggedOut: true,
				isSessionValid: false,
			})
		);
		this.sharedStore.dispatch(
			new fromSharedStore.Go({ path: ['sign-in'] })
		);
		return;
	}
}
